const BASE_URL = `${window.location.protocol}//${window.location.host}`;

/**
 * Get all configuration settings
 */
export async function getConfig() {
    const res = await fetch(`${BASE_URL}/api/config/get_config`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Update configuration settings for a category
 */
export async function setConfigSettings(type, settings) {
    const res = await fetch(`${BASE_URL}/api/config/set_config_settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, settings }),
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Get configuration audit history
 */
export async function getAuditHistory(category = null, limit = 100) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit);

    const queryString = params.toString();
    const url = queryString
        ? `${BASE_URL}/api/config/audit_history?${queryString}`
        : `${BASE_URL}/api/config/audit_history`;

    const res = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Get about information
 */
export async function getAboutInfo() {
    const res = await fetch(`${BASE_URL}/api/config/get_about`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Get multi-SMTP configuration
 */
export async function getSmtpConfig() {
    const res = await fetch(`${BASE_URL}/api/config/get_db_config?type=smtp`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Enable multi-SMTP
 */
export async function enableMultiSmtp() {
    const res = await fetch(`${BASE_URL}/api/config/enable_multi_smtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Add new SMTP configuration
 */
export async function addSmtpConfig(configId, settings) {
    const res = await fetch(`${BASE_URL}/api/config/set_db_config?id=${configId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings, id: -1 }),
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Delete SMTP configuration
 */
export async function deleteSmtpConfig(configId, settingsId) {
    const res = await fetch(`${BASE_URL}/api/config/set_db_config?id=${configId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settingsId }),
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Trigger an event viewer archive (respects date threshold - 30/60/120 days)
 */
export async function archiveNow() {
    const res = await fetch(`${BASE_URL}/api/actions/archive_now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Archive ALL event viewer records immediately (ignores date threshold)
 */
export async function archiveImmediately() {
    const res = await fetch(`${BASE_URL}/api/actions/archive_immediately`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Purge archived records older than retention period
 */
export async function purgeColdArchive() {
    const res = await fetch(`${BASE_URL}/api/actions/purge_cold_archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Delete all records from the cold archive
 */
export async function deleteColdArchive() {
    const res = await fetch(`${BASE_URL}/api/actions/delete_cold_archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}

/**
 * Get the current event viewer archive status
 */
export async function getArchiveStatus() {
    const res = await fetch(`${BASE_URL}/api/actions/archive_status`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
    });

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    if (res.status >= 500) {
        throw new Error(data?.error || `Server error (${res.status})`);
    }
    return { status: res.status, ok: res.ok, data };
}
